abstract class ResultBase<B extends boolean, C extends B extends true ? false : true, T> {
  readonly isSuccess: B
  readonly isFailure: C

  constructor (isSuccess: B, isFailure: C) {
    this.isSuccess = isSuccess
    this.isFailure = isFailure
  }

  abstract logIfError (_message: string): void
  abstract unwrapOrFatal (): T
}

export class Success<T, E extends string> extends ResultBase<true, false, T> {
  readonly value: T

  constructor (value: T) {
    super(true, false)
    this.value = value
  }

  map<T2> (callback: (input: T) => T2): Success<T2, E> {
    const newValue = callback(this.value)
    return new Success(newValue)
  }

  mapError<E2 extends string> (_callback: (input: E) => [E2, string]): Success<T, E2> {
    return Result.success(this.value)
  }

  convert<E2 extends string> (): Result<T, E2> {
    return Result.success(this.value)
  }

  override logIfError (_message: string) {}
  override unwrapOrFatal (): T {
    return this.value
  }
}

class Fail<T, E extends string> extends ResultBase<false, true, T> {
  readonly stack?: string
  readonly moreInfo?: string
  readonly message: string
  readonly error: E

  constructor (error: E, message: string, moreInfo?: string) {
    super(false, true)
    const err = new Error('Fail')
    this.stack = err.stack
    this.message = message
    this.moreInfo = moreInfo
    this.error = error
  }

  ignore () {
    console.debug('Error', this.error, this.message, 'has been ignored')
  }

  log (message: string, payload: object = {}) {
    console.error(message, payload, this.error, this.message, this.stack)
  }

  map<T2> (_callback: (input: T) => T2): Fail<T2, E> {
    return Result.fail(this.error, this.message, this.moreInfo)
  }

  mapError<E2 extends string> (callback: (input: E) => [E2, string]): Fail<T, E2> {
    const [newError, message] = callback(this.error)
    return new Fail(newError, message)
  }

  convert<T2> (): Fail<T2, E> {
    return Result.fail(this.error, this.message, this.moreInfo)
  }

  override toString (): string {
    return `[${this.error}] ${this.message}`
  }

  override logIfError (message: string, payload: object = {}) {
    this.log(message, payload)
  }

  override unwrapOrFatal (): never {
    const error = new Error('Fatal error: ' + this.error + ' ' + this.message)
    console.error(error, this.stack)
    throw error
  }
}

const OkSymbol = Symbol('resultOk')

export type Result<T, E extends string> = Success<T, E> | Fail<T, E>
export type ResultVoid<E extends string> = Success<typeof OkSymbol, E> | Fail<typeof OkSymbol, E>

export const Result = {
  success<T, E extends string> (value: T) {
    return new Success<T, E>(value)
  },
  fail<T, E extends string> (id: E, message: string, moreInfo?: string) {
    return new Fail<T, E>(id, message, moreInfo)
  },
  ok<E extends string> () {
    return new Success<typeof OkSymbol, E>(OkSymbol)
  },
  fromUnknownError<T, E extends string> (id: E, error: unknown): Fail<T, E | 'unknown'> {
    if (error instanceof Error) {
      return new Fail(id, error.message)
    }
    return new Fail('unknown', `An unknown error occured ${error}`)
  },
  fromPossibleResult<T, E extends string> (value: Result<T, E>|T): Result<T, E> {
    if (typeof value === 'object' && value !== null && 'isSuccess' in value) {
      return value
    }
    return Result.success(value)
  }
}

// For testing purposes
export class ExpectResult {
  static toBeSuccessful<T, E extends string> (value: Result<T, E>): asserts value is Success<T, E> {
    if (value.isFailure) throw new Error(`Expected success but got ${value}`)
  }
  static toBeFailure<T, E extends string> (value: Result<T, E>): asserts value is Fail<T, E> {
    if (value.isSuccess) throw new Error(`Expected success but got ${value}`)
  }
}
